import { useState } from 'react'
import { Input } from '../input/Input'
import { Button } from '../button/Button'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import {
  LocationType,
  Address,
  AirportAddress,
  LocationAddress,
  PortAddress,
  isLocationAddress,
  isAirportAddress,
  isPortAddress,
} from '@wove/common'

interface PortSearchResult {
  code: string
  name: string
  countryCode: string
  region: string
}

interface PortOption {
  value: string
  label: string
  portCode: string
  portName: string
  countryCode: string
}

interface AirportSearchResult {
  code: string
  name: string
  city: string
  countryCode: string
  region: string
  coordinates?: {
    lat: number
    lon: number
  } | null
}

interface AirportOption {
  value: string
  label: string
  airportCode: string
  airportName: string
  city?: string
  countryCode: string
}

// Add a list of countries
const COUNTRY_OPTIONS = [
  { value: 'AF', label: 'Afghanistan' },
  { value: 'AL', label: 'Albania' },
  { value: 'DZ', label: 'Algeria' },
  { value: 'AS', label: 'American Samoa' },
  { value: 'AD', label: 'Andorra' },
  { value: 'AO', label: 'Angola' },
  { value: 'AI', label: 'Anguilla' },
  { value: 'AQ', label: 'Antarctica' },
  { value: 'AG', label: 'Antigua and Barbuda' },
  { value: 'AR', label: 'Argentina' },
  { value: 'AM', label: 'Armenia' },
  { value: 'AW', label: 'Aruba' },
  { value: 'AU', label: 'Australia' },
  { value: 'AT', label: 'Austria' },
  { value: 'AZ', label: 'Azerbaijan' },
  { value: 'BS', label: 'Bahamas' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'BB', label: 'Barbados' },
  { value: 'BY', label: 'Belarus' },
  { value: 'BE', label: 'Belgium' },
  { value: 'BZ', label: 'Belize' },
  { value: 'BJ', label: 'Benin' },
  { value: 'BM', label: 'Bermuda' },
  { value: 'BT', label: 'Bhutan' },
  { value: 'BO', label: 'Bolivia' },
  { value: 'BA', label: 'Bosnia and Herzegovina' },
  { value: 'BW', label: 'Botswana' },
  { value: 'BR', label: 'Brazil' },
  { value: 'IO', label: 'British Indian Ocean Territory' },
  { value: 'BN', label: 'Brunei Darussalam' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'BF', label: 'Burkina Faso' },
  { value: 'BI', label: 'Burundi' },
  { value: 'KH', label: 'Cambodia' },
  { value: 'CM', label: 'Cameroon' },
  { value: 'CA', label: 'Canada' },
  { value: 'CV', label: 'Cape Verde' },
  { value: 'KY', label: 'Cayman Islands' },
  { value: 'CF', label: 'Central African Republic' },
  { value: 'TD', label: 'Chad' },
  { value: 'CL', label: 'Chile' },
  { value: 'CN', label: 'China' },
  { value: 'CX', label: 'Christmas Island' },
  { value: 'CC', label: 'Cocos (Keeling) Islands' },
  { value: 'CO', label: 'Colombia' },
  { value: 'KM', label: 'Comoros' },
  { value: 'CG', label: 'Congo' },
  { value: 'CD', label: 'Congo, Democratic Republic of the' },
  { value: 'CK', label: 'Cook Islands' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'CI', label: "Cote D'Ivoire" },
  { value: 'HR', label: 'Croatia' },
  { value: 'CU', label: 'Cuba' },
  { value: 'CW', label: 'Curacao' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'DK', label: 'Denmark' },
  { value: 'DJ', label: 'Djibouti' },
  { value: 'DM', label: 'Dominica' },
  { value: 'DO', label: 'Dominican Republic' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'EG', label: 'Egypt' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'GQ', label: 'Equatorial Guinea' },
  { value: 'ER', label: 'Eritrea' },
  { value: 'EE', label: 'Estonia' },
  { value: 'ET', label: 'Ethiopia' },
  { value: 'FK', label: 'Falkland Islands (Malvinas)' },
  { value: 'FO', label: 'Faroe Islands' },
  { value: 'FJ', label: 'Fiji' },
  { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' },
  { value: 'GF', label: 'French Guiana' },
  { value: 'PF', label: 'French Polynesia' },
  { value: 'TF', label: 'French Southern Territories' },
  { value: 'GA', label: 'Gabon' },
  { value: 'GM', label: 'Gambia' },
  { value: 'GE', label: 'Georgia' },
  { value: 'DE', label: 'Germany' },
  { value: 'GH', label: 'Ghana' },
  { value: 'GI', label: 'Gibraltar' },
  { value: 'GR', label: 'Greece' },
  { value: 'GL', label: 'Greenland' },
  { value: 'GD', label: 'Grenada' },
  { value: 'GP', label: 'Guadeloupe' },
  { value: 'GU', label: 'Guam' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'GG', label: 'Guernsey' },
  { value: 'GN', label: 'Guinea' },
  { value: 'GW', label: 'Guinea-Bissau' },
  { value: 'GY', label: 'Guyana' },
  { value: 'HT', label: 'Haiti' },
  { value: 'VA', label: 'Holy See (Vatican City State)' },
  { value: 'HN', label: 'Honduras' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'HU', label: 'Hungary' },
  { value: 'IS', label: 'Iceland' },
  { value: 'IN', label: 'India' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'IR', label: 'Iran, Islamic Republic of' },
  { value: 'IQ', label: 'Iraq' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IM', label: 'Isle of Man' },
  { value: 'IL', label: 'Israel' },
  { value: 'IT', label: 'Italy' },
  { value: 'JM', label: 'Jamaica' },
  { value: 'JP', label: 'Japan' },
  { value: 'JE', label: 'Jersey' },
  { value: 'JO', label: 'Jordan' },
  { value: 'KZ', label: 'Kazakhstan' },
  { value: 'KE', label: 'Kenya' },
  { value: 'KI', label: 'Kiribati' },
  { value: 'KP', label: "Korea, Democratic People's Republic of" },
  { value: 'KR', label: 'Korea, Republic of' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'KG', label: 'Kyrgyzstan' },
  { value: 'LA', label: "Lao People's Democratic Republic" },
  { value: 'LV', label: 'Latvia' },
  { value: 'LB', label: 'Lebanon' },
  { value: 'LS', label: 'Lesotho' },
  { value: 'LR', label: 'Liberia' },
  { value: 'LY', label: 'Libya' },
  { value: 'LI', label: 'Liechtenstein' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'MO', label: 'Macao' },
  { value: 'MK', label: 'Macedonia, the Former Yugoslav Republic of' },
  { value: 'MG', label: 'Madagascar' },
  { value: 'MW', label: 'Malawi' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'MV', label: 'Maldives' },
  { value: 'ML', label: 'Mali' },
  { value: 'MT', label: 'Malta' },
  { value: 'MH', label: 'Marshall Islands' },
  { value: 'MQ', label: 'Martinique' },
  { value: 'MR', label: 'Mauritania' },
  { value: 'MU', label: 'Mauritius' },
  { value: 'YT', label: 'Mayotte' },
  { value: 'MX', label: 'Mexico' },
  { value: 'FM', label: 'Micronesia, Federated States of' },
  { value: 'MD', label: 'Moldova, Republic of' },
  { value: 'MC', label: 'Monaco' },
  { value: 'MN', label: 'Mongolia' },
  { value: 'ME', label: 'Montenegro' },
  { value: 'MS', label: 'Montserrat' },
  { value: 'MA', label: 'Morocco' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'MM', label: 'Myanmar' },
  { value: 'NA', label: 'Namibia' },
  { value: 'NR', label: 'Nauru' },
  { value: 'NP', label: 'Nepal' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'NC', label: 'New Caledonia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'NI', label: 'Nicaragua' },
  { value: 'NE', label: 'Niger' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'NU', label: 'Niue' },
  { value: 'NF', label: 'Norfolk Island' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'NO', label: 'Norway' },
  { value: 'OM', label: 'Oman' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'PW', label: 'Palau' },
  { value: 'PS', label: 'Palestine, State of' },
  { value: 'PA', label: 'Panama' },
  { value: 'PG', label: 'Papua New Guinea' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'PE', label: 'Peru' },
  { value: 'PH', label: 'Philippines' },
  { value: 'PN', label: 'Pitcairn' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'QA', label: 'Qatar' },
  { value: 'RE', label: 'Reunion' },
  { value: 'RO', label: 'Romania' },
  { value: 'RU', label: 'Russian Federation' },
  { value: 'RW', label: 'Rwanda' },
  { value: 'BL', label: 'Saint Barthelemy' },
  { value: 'SH', label: 'Saint Helena' },
  { value: 'KN', label: 'Saint Kitts and Nevis' },
  { value: 'LC', label: 'Saint Lucia' },
  { value: 'MF', label: 'Saint Martin (French part)' },
  { value: 'PM', label: 'Saint Pierre and Miquelon' },
  { value: 'VC', label: 'Saint Vincent and the Grenadines' },
  { value: 'WS', label: 'Samoa' },
  { value: 'SM', label: 'San Marino' },
  { value: 'ST', label: 'Sao Tome and Principe' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'SN', label: 'Senegal' },
  { value: 'RS', label: 'Serbia' },
  { value: 'SC', label: 'Seychelles' },
  { value: 'SL', label: 'Sierra Leone' },
  { value: 'SG', label: 'Singapore' },
  { value: 'SX', label: 'Sint Maarten (Dutch part)' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'SB', label: 'Solomon Islands' },
  { value: 'SO', label: 'Somalia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'GS', label: 'South Georgia and the South Sandwich Islands' },
  { value: 'SS', label: 'South Sudan' },
  { value: 'ES', label: 'Spain' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'SD', label: 'Sudan' },
  { value: 'SR', label: 'Suriname' },
  { value: 'SJ', label: 'Svalbard and Jan Mayen' },
  { value: 'SZ', label: 'Swaziland' },
  { value: 'SE', label: 'Sweden' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'SY', label: 'Syrian Arab Republic' },
  { value: 'TW', label: 'Taiwan, Province of China' },
  { value: 'TJ', label: 'Tajikistan' },
  { value: 'TZ', label: 'Tanzania, United Republic of' },
  { value: 'TH', label: 'Thailand' },
  { value: 'TL', label: 'Timor-Leste' },
  { value: 'TG', label: 'Togo' },
  { value: 'TK', label: 'Tokelau' },
  { value: 'TO', label: 'Tonga' },
  { value: 'TT', label: 'Trinidad and Tobago' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'TR', label: 'Turkey' },
  { value: 'TM', label: 'Turkmenistan' },
  { value: 'TC', label: 'Turks and Caicos Islands' },
  { value: 'TV', label: 'Tuvalu' },
  { value: 'UG', label: 'Uganda' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'UM', label: 'United States Minor Outlying Islands' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'UZ', label: 'Uzbekistan' },
  { value: 'VU', label: 'Vanuatu' },
  { value: 'VE', label: 'Venezuela' },
  { value: 'VN', label: 'Viet Nam' },
  { value: 'VG', label: 'Virgin Islands, British' },
  { value: 'VI', label: 'Virgin Islands, U.s.' },
  { value: 'WF', label: 'Wallis and Futuna' },
  { value: 'EH', label: 'Western Sahara' },
  { value: 'YE', label: 'Yemen' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'ZW', label: 'Zimbabwe' },
]

interface AddressEditorProps {
  value: Address
  onChange: (value: Address) => void
  type: string
  isDisabled?: boolean
  searchPorts?: (query: string) => Promise<PortSearchResult[]>
  searchAirports?: (query: string) => Promise<AirportSearchResult[]>
}

const AddressEditor = ({
  value,
  onChange,
  type,
  isDisabled = false,
  searchPorts,
  searchAirports,
}: AddressEditorProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const formatAddress = (addr: any, type: string) => {
    if (!addr)
      return type === LocationType.PORT
        ? 'Click to select port'
        : type === LocationType.AIRPORT
        ? 'Click to select airport'
        : 'Click to add address'

    // Handle address type
    if (type === 'Door') {
      const parts = [addr.address1, addr.address2, addr.city, addr.province, addr.postalCode, addr.country].filter(
        Boolean,
      )
      return parts.length > 0 ? parts.join(', ') : 'Click to add address'
    }

    // Handle port type
    if (type === LocationType.PORT) {
      return addr.portCode ? `${addr.portName} (${addr.portCode})` : 'Click to select port'
    }

    // Handle airport type
    if (type === LocationType.AIRPORT) {
      return addr.airportCode
        ? `${addr.airportName}${addr.city ? ` - ${addr.city}` : ''} (${addr.airportCode})`
        : 'Click to select airport'
    }

    // If addr exists but doesn't match expected types, it might be a partial update
    if (typeof addr === 'object') {
      const parts = [addr.address1, addr.address2, addr.city, addr.province, addr.postalCode, addr.country].filter(
        Boolean,
      )
      return parts.length > 0 ? parts.join(', ') : 'Click to add address'
    }

    return type === LocationType.PORT
      ? 'Click to select port'
      : type === LocationType.AIRPORT
      ? 'Click to select airport'
      : 'Click to add address'
  }

  if (!isExpanded) {
    return (
      <div className="relative w-full" onClick={() => !isDisabled && setIsExpanded(true)}>
        <Input
          placeholder={`Click to ${type === 'Door' ? 'add address' : `select ${type.toLowerCase()}`}`}
          value={formatAddress(value, type)}
          readOnly
          disabled={isDisabled}
          className={isDisabled ? '' : 'cursor-pointer'}
        />
      </div>
    )
  }

  switch (type) {
    case LocationType.PORT:
      const loadPortOptions = async (inputValue: string) => {
        console.log('Loading port options for:', inputValue)
        if (!inputValue || !searchPorts) return [] as PortOption[]

        try {
          const data: PortSearchResult[] = await searchPorts(inputValue)

          return data.map(
            (port): PortOption => ({
              value: port.code,
              label: `${port.name} (${port.code})`,
              portCode: port.code,
              portName: port.name,
              countryCode: port.countryCode,
            }),
          )
        } catch (error) {
          console.error('Error loading ports:', error)
          return [] as PortOption[]
        }
      }

      return (
        <AsyncSelect<PortOption>
          options={[]}
          value={
            isPortAddress(value)
              ? {
                  value: value.portCode,
                  label: `${value.portName} (${value.portCode})`,
                  portCode: value.portCode,
                  portName: value.portName,
                  countryCode: value.country,
                }
              : null
          }
          onChange={selected => {
            const port = selected as PortOption
            onChange({
              type: LocationType.PORT,
              portCode: port.portCode,
              portName: port.portName,
              country: port.countryCode,
            })
          }}
          loadOptions={loadPortOptions}
          placeholder="Search ports..."
          className="text-sm"
          classNamePrefix="react-select"
          components={{
            LoadingMessage: () => <div className="p-2 text-sm text-gray-500">Loading ports...</div>,
            NoOptionsMessage: ({ selectProps }) => (
              <div className="p-2 text-sm text-gray-500">
                {selectProps.inputValue ? 'No ports found' : 'Type to search ports'}
              </div>
            ),
          }}
          cacheOptions
          defaultOptions
          loadingMessage={() => 'Loading ports...'}
          noOptionsMessage={({ inputValue }) => (inputValue ? 'No ports found' : 'Type to search ports')}
          isDisabled={isDisabled}
        />
      )

    case LocationType.AIRPORT:
      const loadAirportOptions = async (inputValue: string) => {
        console.log('Loading airport options for:', inputValue)
        if (!inputValue || !searchAirports) return []

        try {
          const data: AirportSearchResult[] = await searchAirports(inputValue)

          return data.map(
            (airport): AirportOption => ({
              value: airport.code,
              label: `${airport.name}${airport.city ? ` - ${airport.city}` : ''} (${airport.code})`,
              airportCode: airport.code,
              airportName: airport.name,
              city: airport.city,
              countryCode: airport.countryCode,
            }),
          )
        } catch (error) {
          console.error('Error loading airports:', error)
          return []
        }
      }

      return (
        <AsyncSelect<AirportOption>
          options={[]}
          value={
            isAirportAddress(value)
              ? {
                  value: value.airportCode,
                  label: `${value.airportName} (${value.airportCode})`,
                  airportCode: value.airportCode,
                  airportName: value.airportName,
                  countryCode: value.country,
                }
              : null
          }
          onChange={selected => {
            const airport = selected as AirportOption
            onChange({
              type: LocationType.AIRPORT,
              airportCode: airport.airportCode,
              airportName: airport.airportName,
              country: airport.countryCode,
            })
          }}
          loadOptions={loadAirportOptions}
          placeholder="Search airports..."
          className="text-sm"
          classNamePrefix="react-select"
          components={{
            LoadingMessage: () => <div className="p-2 text-sm text-gray-500">Loading airports...</div>,
            NoOptionsMessage: ({ selectProps }) => (
              <div className="p-2 text-sm text-gray-500">
                {selectProps.inputValue ? 'No airports found' : 'Type to search airports'}
              </div>
            ),
          }}
          cacheOptions
          defaultOptions
          loadingMessage={() => 'Loading airports...'}
          noOptionsMessage={({ inputValue }) => (inputValue ? 'No airports found' : 'Type to search airports')}
          isDisabled={isDisabled}
        />
      )

    case LocationType.DOOR:
      const locationValue = value as LocationAddress
      return (
        <div className="space-y-2">
          <Input
            placeholder="Address Line 1"
            value={locationValue.address1}
            onChange={e => {
              onChange({
                ...locationValue,
                address1: e.target.value,
                address2: locationValue?.address2 || '',
                city: locationValue?.city || '',
                province: locationValue?.province || '',
                postalCode: locationValue?.postalCode || '',
                country: locationValue?.country || '',
              })
            }}
            disabled={isDisabled}
          />
          <Input
            placeholder="Address Line 2"
            value={locationValue.address2}
            onChange={e => {
              onChange({
                ...locationValue,
                address2: e.target.value,
                address1: locationValue?.address1 || '',
                city: locationValue?.city || '',
                province: locationValue?.province || '',
                postalCode: locationValue?.postalCode || '',
                country: locationValue?.country || '',
              })
            }}
            disabled={isDisabled}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="City"
              value={locationValue.city}
              onChange={e => {
                onChange({
                  ...locationValue,
                  city: e.target.value,
                })
              }}
              disabled={isDisabled}
            />
            <Input
              placeholder="Province/State"
              value={locationValue.province}
              onChange={e => {
                onChange({
                  ...locationValue,
                  province: e.target.value,
                })
              }}
              disabled={isDisabled}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Postal Code"
              value={locationValue.postalCode}
              onChange={e => {
                onChange({
                  ...locationValue,
                  postalCode: e.target.value,
                })
              }}
              disabled={isDisabled}
            />
            <Select
              options={COUNTRY_OPTIONS}
              value={COUNTRY_OPTIONS.find(option => option.value === (value as LocationAddress)?.country) || null}
              onChange={selectedOption => {
                onChange({
                  ...value,
                  country: selectedOption ? selectedOption.value : '',
                } as LocationAddress)
              }}
              placeholder="Country..."
              className="text-sm"
              classNamePrefix="react-select"
              styles={{
                control: base => ({
                  ...base,
                  '&:hover': {
                    minWidth: 'max-content',
                    width: 'auto',
                    zIndex: 1,
                  },
                }),
              }}
              isClearable={true}
              isSearchable={true}
              isDisabled={isDisabled}
            />
          </div>
          <Button variant="outline" onClick={() => setIsExpanded(false)}>
            Done
          </Button>
        </div>
      )
  }

  return null
}

export default AddressEditor
